import React, { FunctionComponent, useCallback, useEffect } from 'react';

import cn from 'classnames';
import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../../../../init-store';
import { isBroadcastAllowed, selectIsBroadcastEnabled } from '../../../../pages/call/selectors';
import { SvgImage } from '../../../../shared/components';
import Button from '../../../../shared/components/button';
import Spin from '../../../../shared/components/spin';
import StartScreenLayout from '../../../../shared/components/start-screen-layout';
import { isMobileUa } from '../../../../shared/features/uatraits/selectors';
import { isYandexSsoUserSelector } from '../../../../shared/features/user/selectors';
import { i18n } from '../../../../shared/helpers/i18n';
import { ACTION, FEATURE } from '../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { countMetrika, reachGoal } from '../../../../shared/helpers/metrika';
import { connectCall, createBroadcastForm, createCall, planCall } from '../../features/create-call/slice';
import { TouchNativeAppBanner } from '../touch-native-app-banner';
import { WebNativeAppBanner } from '../web-native-app-banner';
import css from './index.css';

const logger = createLogger('main-screen');

const mapDispatch = {
    createCall,
    connectCall,
    planCall,
    createBroadcastForm,
};

const connector = connect((state: RootState) => {
    return {
        isBroadcastAllowed: isBroadcastAllowed(state),
        isBroadcastEnabled: selectIsBroadcastEnabled(state),
        isMobile: isMobileUa(state),
        isYandexSsoUser: isYandexSsoUserSelector(state),
    };
}, mapDispatch);

type Props = ConnectedProps<typeof connector> & { autoCreate?: boolean };

interface ButtonsLayoutProps {
    onCreateCallClick: () => void;
    onConnectCallClick: () => void;
    onPlanCallClick: () => void;
    onCreateBroadcastClick: () => void;
    isBroadcastEnabled?: boolean;
    isPlanningHidden?: boolean;
    isPlanningDisabled?: boolean;
    isMobile: boolean;
}

const ThreeButtonsLayout: FunctionComponent<ButtonsLayoutProps> = ({
    onCreateCallClick,
    onConnectCallClick,
    onPlanCallClick,
    isPlanningHidden,
    isPlanningDisabled,
    isMobile,
}) => (
    <div className={cn(css.contentWrapper, { [css.isWeb]: !isMobile })}>
        <div className={css.ButtonsContainer}>
            <Button
                className={css.CreateCallButton}
                view="neutral"
                onClick={onCreateCallClick}
                testId="create-call-button"
            >
                <div className={css.ButtonContent}>
                    <SvgImage
                        imageName="cameraLens"
                        className={css.cameraLensIcon}
                        style={{ width: 95, height: 100 }}
                        fill="none"
                    />
                    <span className={css.CreateCallContentLabel}>{i18n('create-call', 'create-call-text')}</span>
                </div>
            </Button>
            <div className={css.ButtonsConnectPlanContainer}>
                <Button className={css.ConnectCallButton} view="neutral" onClick={onConnectCallClick}>
                    <div className={css.ButtonContent}>
                        <SvgImage
                            imageName="users"
                            className={css.usersIcon}
                            style={{ width: 48, height: 48 }}
                            fill="none"
                        />
                        <span className={css.ConnectPlanCallContentLabel}>
                            {i18n('create-call', 'connect-call-text')}
                        </span>
                    </div>
                </Button>
                {!isPlanningHidden && (
                    <Button
                        className={css.PlanCallButton}
                        view="neutral"
                        onClick={onPlanCallClick}
                        isDisabled={isPlanningDisabled}
                    >
                        <div className={css.ButtonContent}>
                            <SvgImage imageName="calendar" style={{ width: 48, height: 48 }} fill="none" />
                            <span className={css.ConnectPlanCallContentLabel}>
                                {i18n('create-call', 'plan-call-text')}
                            </span>
                        </div>
                    </Button>
                )}
            </div>
        </div>
        {isMobile ? <TouchNativeAppBanner /> : <WebNativeAppBanner />}
    </div>
);

const FourButtonsLayout: FunctionComponent<ButtonsLayoutProps> = ({
    onCreateCallClick,
    onConnectCallClick,
    onPlanCallClick,
    onCreateBroadcastClick,
    isBroadcastEnabled,
    isPlanningHidden,
    isPlanningDisabled,
    isMobile,
}) => (
    <div className={cn(css.contentWrapper, { [css.isWeb]: !isMobile })}>
        <div className={css.FourButtonsLayout}>
            <Button
                className={cn(css.FourButtonsLayout__Button, css.FourButtonsLayout__CreateCallButton)}
                view="neutral"
                onClick={onCreateCallClick}
                testId="create-call-button"
            >
                <div className={css.ButtonContent}>
                    <SvgImage imageName="newCall" className={css.FourButtonsLayout__Icon} />
                    <div className={css.FourButtonsLayout__Text}>{i18n('create-call', 'new-call-text')}</div>
                    <div className={css.FourButtonsLayout__Description}>
                        {i18n('create-call', 'new-call-description')}
                    </div>
                </div>
            </Button>
            <Button
                className={css.FourButtonsLayout__Button}
                view="neutral"
                onClick={onCreateBroadcastClick}
                isDisabled={!isBroadcastEnabled}
            >
                <div className={css.ButtonContent}>
                    <SvgImage
                        imageName={isBroadcastEnabled ? 'broadcastBig' : 'broadcastBigDisabled'}
                        className={css.FourButtonsLayout__Icon}
                    />
                    <div className={css.FourButtonsLayout__Text}>{i18n('create-call', 'broadcast-text')}</div>
                    <div className={css.FourButtonsLayout__Description}>
                        {isBroadcastEnabled
                            ? i18n('create-call', 'broadcast-description')
                            : i18n('create-call', 'disabled-broadcast-description')}
                    </div>
                </div>
            </Button>
            {!isPlanningHidden && (
                <Button
                    className={css.FourButtonsLayout__Button}
                    view="neutral"
                    onClick={onPlanCallClick}
                    isDisabled={isPlanningDisabled}
                >
                    <div className={css.ButtonContent}>
                        <SvgImage imageName="calendar" className={css.FourButtonsLayout__Icon} />
                        <div className={css.FourButtonsLayout__Text}>{i18n('create-call', 'plan-call-text')}</div>
                        <div className={css.FourButtonsLayout__Description}>
                            {i18n('create-call', 'plan-call-description')}
                        </div>
                    </div>
                </Button>
            )}
            <Button
                className={cn(css.FourButtonsLayout__Button, css.FourButtonsLayout__ConnectCallButton)}
                view="neutral"
                onClick={onConnectCallClick}
            >
                <div className={css.ButtonContent}>
                    <SvgImage imageName="users" className={css.FourButtonsLayout__Icon} />
                    <div className={css.FourButtonsLayout__Text}>{i18n('create-call', 'connect-call-text')}</div>
                    <div className={css.FourButtonsLayout__Description}>
                        {i18n('create-call', 'connect-call-description')}
                    </div>
                </div>
            </Button>
        </div>
        {isMobile ? <TouchNativeAppBanner fourButtonsLayout={true} /> : <WebNativeAppBanner fourButtonsLayout={true} />}
    </div>
);

const CreateCall: FunctionComponent<Props> = ({
    createCall,
    connectCall,
    planCall,
    autoCreate,
    createBroadcastForm,
    isBroadcastAllowed,
    isBroadcastEnabled,
    isMobile,
    isYandexSsoUser,
}) => {
    useEffect(() => {
        if (autoCreate) {
            createCall();
        }
    }, [autoCreate, createCall]);

    const onCreateCallClick = useCallback(() => {
        logger.startFeatureDelta({ feature: FEATURE.CONNECTION, action: ACTION.CREATE_CONFERENCE });
        reachGoal('target');
        createCall();
    }, [createCall]);

    const onConnectCallClick = useCallback(() => {
        connectCall();
    }, [connectCall]);

    const onPlanCallClick = useCallback(() => {
        planCall();
    }, [planCall]);

    const onCreateBroadcastClick = useCallback(() => {
        createBroadcastForm();
        countMetrika('main_screen', 'new_stream', 'click');
        logger.event('Broadcast create button clicked', {
            additional: {
                method: 'onCreateBroadcastClick',
            },
        });
    }, [createBroadcastForm]);

    return (
        <StartScreenLayout>
            <div className={css.MainScreen}>
                <div className={css.Content}>
                    {!autoCreate && (
                        <h1 className={cn(css.mainTitle, isMobile ? css.middleMarginBottom : css.largeMarginBottom)}>
                            {i18n('create-call', 'main-title-v2')}
                        </h1>
                    )}
                    {autoCreate ? (
                        <Spin className={css.Loader} size="xl" color="white" progress />
                    ) : isBroadcastAllowed ? (
                        <FourButtonsLayout
                            onCreateCallClick={onCreateCallClick}
                            onConnectCallClick={onConnectCallClick}
                            onPlanCallClick={onPlanCallClick}
                            onCreateBroadcastClick={onCreateBroadcastClick}
                            isBroadcastEnabled={isBroadcastEnabled}
                            isPlanningHidden={isMobile}
                            isPlanningDisabled={isYandexSsoUser}
                            isMobile={isMobile}
                        />
                    ) : (
                        <ThreeButtonsLayout
                            onCreateCallClick={onCreateCallClick}
                            onConnectCallClick={onConnectCallClick}
                            onPlanCallClick={onPlanCallClick}
                            onCreateBroadcastClick={onCreateBroadcastClick}
                            isPlanningHidden={isMobile}
                            isPlanningDisabled={isYandexSsoUser}
                            isMobile={isMobile}
                        />
                    )}
                </div>
            </div>
        </StartScreenLayout>
    );
};

export default connector(CreateCall);
